import {
  Delete,
  Edit,
  EditNote,
  FileDownload,
  Image,
  MenuOpen,
  Newspaper,
  UploadFile,
} from '@mui/icons-material'
import { Button, Divider, IconButton, useTheme } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_TableOptions,
  useMaterialReactTable,
} from 'material-react-table'
import { useConfirm } from 'material-ui-confirm'
import React, { FunctionComponent, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import AuditIconButton from '../../../../base/components/Auditoria/AuditIconButton'
import MuiRenderTopToolbarCustomActions from '../../../../base/components/MuiTable/MuiRenderTopToolbarCustomActions'
import SimpleMenu, { SimpleMenuItem } from '../../../../base/components/MyMenu/SimpleMenu'
import StackMenu from '../../../../base/components/MyMenu/StackMenu'
import { StackMenuItem } from '../../../../base/components/MyMenu/StackMenuItem'
import { PAGE_DEFAULT, PageProps } from '../../../../interfaces'
import { genPath } from '../../../../utils/genPath'
import { genApiQuery } from '../../../../utils/helper'
import { MuiToolbarAlertBannerProps } from '../../../../utils/muiTable/materialReactTableUtils'
import { MuiTableAdvancedOptionsProps } from '../../../../utils/muiTable/muiTableAdvancedOptionsProps'
import { notDanger, notSuccess } from '../../../../utils/notification'
import { swalClose, swalException, swalLoading } from '../../../../utils/swal'
import { apiArticuloEliminar } from '../../api/apiArticuloEliminar'
import { articuloRoutesMap } from '../../articuloRoutes'
import { articuloColumns } from './articuloColumns'
import ArticuloImportarDialog from './ArticuloImportarDialog'
import ArticuloImportarImagenDialog from './ArticuloImportarImagenDialog'
import { apiArticuloListado } from '../../api/apiArticuloListado'
import { exportarArticulosCSV } from '../../services/articuloExportar'
import ArticuloActualizarCSVDialog from './ArticuloActualizarCSVDialog'
import { ArticuloProps } from '../../../../interfaces/articulo.ts'

interface OwnProps {}

type Props = OwnProps

/**
 * Listado o datatable de articulos
 * @constructor
 */
const ArticuloListado: FunctionComponent<Props> = () => {
  const columns = useMemo<MRT_ColumnDef<ArticuloProps>[]>(() => articuloColumns, [])
  const theme = useTheme()
  const confirm = useConfirm()
  const [importarImagen, setImportarImagen] = useState<{
    activo: boolean
    row: ArticuloProps | null
  }>({
    activo: false,
    row: null,
  })

  const [importarArticulos, setImportarArticulos] = useState(false)
  const [actualizarArticulos, setActualizarArticulos] = useState(false)

  // ESTADO DATATABLE
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  // const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: PAGE_DEFAULT.page,
    pageSize: PAGE_DEFAULT.limit,
  })
  const [rowCount, setRowCount] = useState<number>(0)
  const [globalFilter, setGlobalFilter] = useState('')
  // FIN ESTADO DATATABLE

  // API FETCH
  const { data, isError, isRefetching, isLoading, refetch } = useQuery<ArticuloProps[]>({
    queryKey: [
      'articulos',
      columnFilters,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
    ],
    queryFn: async () => {
      const query = genApiQuery(columnFilters)
      const fetchPagination: PageProps = {
        ...PAGE_DEFAULT,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        reverse: sorting.length <= 0,
        query,
      }
      const { pageInfo, docs } = await apiArticuloListado(fetchPagination)
      setRowCount(pageInfo.totalDocs)
      return docs
    },
    refetchOnWindowFocus: true,
    refetchInterval: false,
  })

  // Query para traer todos los datos sin restricciones
  const { data: dataAll, isLoading: isLoadingAll } = useQuery<ArticuloProps[]>({
    queryKey: ['articulos-all'],
    queryFn: async () => {
      const fetchPagination: PageProps = {
        ...PAGE_DEFAULT,
        page: 1,
        limit: 100000,
        reverse: true,
        query: '',
      }
      const { docs } = await apiArticuloListado(fetchPagination)
      return docs
    },
    refetchOnWindowFocus: false,
    refetchInterval: false,
  })
  // FIN API FETCH
  // Eliminación del articulo
  const eliminarProducto = async (row: ArticuloProps) => {
    const { confirmed } = await confirm({
      content: (
        <>
          Confirma que desea eliminar el articulo{' '}
          <strong>
            {row.codigoArticulo} - {row.nombreArticulo}
          </strong>
        </>
      ),
    })
    if (!confirmed) return
    swalLoading()
    const resp = await apiArticuloEliminar(1, row.codigoArticulo).catch((e) =>
      swalException(e),
    )
    if (resp) {
      notSuccess()
      await refetch()
    }
    swalClose()
  }

  const exportarDatos = async () => {
    swalLoading()
    try {
      if (!dataAll || dataAll.length === 0) {
        notDanger('No hay datos para exportar.')
        return
      }
      await exportarArticulosCSV(dataAll, false)
    } catch (error: any) {
      console.error(error)
      notDanger(error.message || 'Error al exportar los datos.')
    } finally {
      swalClose()
    }
  }

  const table = useMaterialReactTable({
    ...(MuiTableAdvancedOptionsProps as MRT_TableOptions<ArticuloProps>),
    columns,
    data: data || [],
    muiToolbarAlertBannerProps: MuiToolbarAlertBannerProps(isError),
    state: {
      isLoading,
      columnFilters,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      density: 'compact',
      sorting,
    },
    renderTopToolbarCustomActions: () => (
      <MuiRenderTopToolbarCustomActions refetch={refetch} />
    ),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    rowCount,
    positionToolbarAlertBanner: 'bottom',
    renderRowActions: ({ row }) => (
      <>
        <SimpleMenu
          menuButton={
            <IconButton aria-label="menuGestionRoles" sx={{ p: 0.7 }}>
              <MenuOpen />
            </IconButton>
          }
        >
          <SimpleMenuItem
            color={'primary'}
            onClick={() => setImportarImagen({ activo: true, row: row.original })}
          >
            <Image />
            Importar Imagen
          </SimpleMenuItem>
          <Divider />
          <SimpleMenuItem
            color={theme.palette.error.main}
            sx={{ color: theme.palette.error.main }}
            onClick={() => eliminarProducto(row.original)}
          >
            <Delete />
            Eliminar
          </SimpleMenuItem>
        </SimpleMenu>
        <IconButton
          component={Link}
          to={genPath(articuloRoutesMap.actualizar.path, { id: row.original._id })}
          sx={{ color: theme.palette.primary.main, p: 0.7 }}
        >
          <Edit />
        </IconButton>
        <AuditIconButton row={row.original} />
      </>
    ),
  })

  return (
    <>
      <StackMenu asideSidebarFixed>
        <StackMenuItem>
          <Button
            variant="contained"
            onClick={() => setActualizarArticulos(true)}
            startIcon={<EditNote />}
            color="info"
          >
            Actualización Masiva
          </Button>
        </StackMenuItem>
        <StackMenuItem>
          <Button
            variant="outlined"
            onClick={exportarDatos}
            startIcon={<FileDownload />}
            color="success"
          >
            Exportar
          </Button>
        </StackMenuItem>
        <StackMenuItem>
          <Button
            variant="contained"
            onClick={() => setImportarArticulos(true)}
            startIcon={<UploadFile />}
            color="secondary"
          >
            Importar
          </Button>
        </StackMenuItem>
        <StackMenuItem>
          <Button
            variant="contained"
            component={Link}
            to={articuloRoutesMap.registro.path}
            startIcon={<Newspaper />}
            color="primary"
          >
            {articuloRoutesMap.registro.name}
          </Button>
        </StackMenuItem>
      </StackMenu>

      <MaterialReactTable table={table} />
      <ArticuloImportarImagenDialog
        id={'importarImagen'}
        keepMounted={false}
        open={importarImagen.activo}
        row={importarImagen.row}
        onClose={async (value?) => {
          if (value) await refetch()
          setImportarImagen({ activo: false, row: null })
        }}
      />

      <ArticuloImportarDialog
        id={'importarArticulo'}
        keepMounted={false}
        open={importarArticulos}
        onClose={async (value?: boolean) => {
          if (value) await refetch()
          setImportarArticulos(false)
        }}
      />
      <ArticuloActualizarCSVDialog
        id={'actualizarArticulo'}
        keepMounted={false}
        data={dataAll || []}
        open={actualizarArticulos}
        onClose={async (value?: boolean) => {
          await refetch()
          setActualizarArticulos(false)
        }}
      />
    </>
  )
}

export default ArticuloListado
